/**
 * "What's left before export", above the timeline: the live checks one at a time, each with its fix, and a clear
 * "Ready to export" when nothing is left (Fogg: the next prompt after the draft plays is the finishing path, and
 * progress — not editing effort — is what gets celebrated).
 */
import { useEffect, useState } from 'react';
import { CheckCircle2, ChevronLeft, ChevronRight, Download, Lightbulb, Sparkles, Wand2 } from 'lucide-react';
import type { FinishCheck } from './finishChecks';

export function FinishBar({ checks, suggestions = [], busy, onFix, onFixAll, onGo, onExport }: {
  /** Rules: what stands between the draft and export. */ checks: FinishCheck[];
  /** The concept's suggestions: shown once the rules are met, never counted as "to finish". */ suggestions?: FinishCheck[]; busy: boolean;
  onFix: (c: FinishCheck) => void; onFixAll: () => void; onGo: (scene: number) => void; onExport: () => void;
}) {
  const [at, setAt] = useState(0);
  const [tip, setTip] = useState(0);
  useEffect(() => { if (at >= checks.length) setAt(Math.max(0, checks.length - 1)); }, [checks.length, at]);
  useEffect(() => { if (tip >= suggestions.length) setTip(Math.max(0, suggestions.length - 1)); }, [suggestions.length, tip]);
  const codeFixes = checks.filter((c) => c.fix && 'apply' in c.fix).length;

  if (!checks.length) {
    const s = suggestions[Math.min(tip, suggestions.length - 1)];
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-3 rounded-xl border border-[#c6f37a] bg-[#f7fde9] px-3.5 py-2">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-[#3b7d0b]" />
          <span className="flex-1 text-[13px] text-[#1f3d06]"><b>Ready to export.</b> Every line reads in time and the sound is balanced.</span>
          <button type="button" disabled={busy} onClick={onExport} className="inline-flex items-center gap-1.5 rounded-lg bg-[#2e024c] px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#46236a] disabled:opacity-40"><Download className="h-3.5 w-3.5 text-[#a9f600]" />Export MP4</button>
        </div>
        {s && (
          // The concept's ideas: direction, not errors — neutral, optional, one at a time.
          <div className="flex items-center gap-2 px-1 text-[12px] text-[#667085]">
            <Lightbulb className="h-3.5 w-3.5 shrink-0 text-[#98a2b3]" />
            {suggestions.length > 1 && <button type="button" aria-label="Next idea" onClick={() => setTip((tip + 1) % suggestions.length)} className="shrink-0 tabular-nums hover:text-[#344054]">{tip + 1}/{suggestions.length}</button>}
            <button type="button" onClick={() => s.scene != null && onGo(s.scene)} className="min-w-0 flex-1 truncate text-left hover:underline" title={s.text}>{s.text}</button>
            {s.fix && <button type="button" disabled={busy} onClick={() => onFix(s)} className="shrink-0 rounded-md px-2 py-0.5 font-semibold text-[#475467] hover:bg-[#f2f4f7] disabled:opacity-40">{s.fix.label}</button>}
          </div>
        )}
      </div>
    );
  }
  const c = checks[Math.min(at, checks.length - 1)];
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-[#fedf89] bg-[#fffcf5] px-3 py-2">
      <span className="shrink-0 rounded-full bg-[#fef0c7] px-2 py-0.5 text-[11.5px] font-bold text-[#93370d]">{checks.length} to finish</span>
      {checks.length > 1 && (
        <span className="flex shrink-0 items-center text-[#b54708]">
          <button type="button" aria-label="Previous" onClick={() => setAt((at - 1 + checks.length) % checks.length)} className="rounded p-0.5 hover:bg-[#fef0c7]"><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-[11px] tabular-nums">{at + 1}/{checks.length}</span>
          <button type="button" aria-label="Next" onClick={() => setAt((at + 1) % checks.length)} className="rounded p-0.5 hover:bg-[#fef0c7]"><ChevronRight className="h-4 w-4" /></button>
        </span>
      )}
      <button type="button" onClick={() => c.scene != null && onGo(c.scene)} className="min-w-0 flex-1 truncate text-left text-[13px] text-[#7a2e0e] hover:underline" title={c.text}>{c.text}</button>
      {c.fix && (
        <button type="button" disabled={busy} onClick={() => onFix(c)} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#2e024c] px-2.5 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#46236a] disabled:opacity-40">
          {'lykke' in c.fix ? <Sparkles className="h-3.5 w-3.5 text-[#ff9cff]" /> : <Wand2 className="h-3.5 w-3.5 text-[#a9f600]" />}{c.fix.label}
        </button>
      )}
      {codeFixes > 1 && <button type="button" disabled={busy} onClick={onFixAll} title={`Applies: ${checks.filter((x) => x.fix && 'apply' in x.fix).map((x) => x.fix!.label.toLowerCase()).join(', ')} — undo with ⌘Z`} className="shrink-0 rounded-lg border border-[#fedf89] bg-white px-2.5 py-1.5 text-[12.5px] font-semibold text-[#93370d] hover:bg-[#fef0c7] disabled:opacity-40">Fix {codeFixes} at once</button>}
    </div>
  );
}
